import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { supabase } from "@/integrations/db/client";
import { useFeature } from "@/hooks/useTenant";
import {
  channelOn,
  NOTIFICATION_KINDS,
  type NotificationChannel,
  type NotificationKind,
  type PreferenceRow,
} from "@/lib/notificationKinds";

const PREFS_KEY = ["notification-preferences"];

/**
 * The signed-in person's notification settings in this tenant (#9112). Only
 * rows they changed exist; read them through channelOn(), which applies the
 * all-on default. Not fetched at all on a tenant without the notifications flag.
 */
export const useNotificationPreferences = () => {
  const enabled = useFeature("notifications");
  return useQuery({
    queryKey: PREFS_KEY,
    enabled,
    queryFn: async (): Promise<PreferenceRow[]> => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return [];
      const { data, error } = await supabase
        .from("notification_preferences")
        .select("kind, in_app, email")
        .eq("user_id", user.id);
      if (error) throw error;
      return data ?? [];
    },
  });
};

/**
 * Whether a kind should show in the app. Always true on a tenant without the
 * notifications flag, where nothing about notifications is configurable and the
 * existing in-app behaviour stands.
 */
export const useInAppEnabled = (kind: NotificationKind): boolean => {
  const enabled = useFeature("notifications");
  const { data = [] } = useNotificationPreferences();
  return !enabled || channelOn(data, kind, "in_app");
};

interface SetPreference {
  /** One kind, or every kind (the settings page's "all email off"). */
  kinds: readonly NotificationKind[];
  channel: NotificationChannel;
  value: boolean;
}

export const useSetNotificationPreference = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ kinds, channel, value }: SetPreference) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Upsert whole rows: the untouched channel keeps its current value, so a
      // first change to a kind does not reset the other channel to its default.
      const current = queryClient.getQueryData<PreferenceRow[]>(PREFS_KEY) ?? [];
      const rows = kinds.map((kind) => ({
        user_id: user.id,
        kind,
        in_app: channel === "in_app" ? value : channelOn(current, kind, "in_app"),
        email: channel === "email" ? value : channelOn(current, kind, "email"),
        updated_at: new Date().toISOString(),
      }));
      const { error } = await supabase
        .from("notification_preferences")
        .upsert(rows, { onConflict: "tenant_id,user_id,kind" });
      if (error) throw error;
    },
    onMutate: async ({ kinds, channel, value }) => {
      await queryClient.cancelQueries({ queryKey: PREFS_KEY });
      const previous = queryClient.getQueryData<PreferenceRow[]>(PREFS_KEY);
      queryClient.setQueryData<PreferenceRow[]>(PREFS_KEY, (old = []) => {
        const next = NOTIFICATION_KINDS.map((kind) => ({
          kind,
          in_app: channelOn(old, kind, "in_app"),
          email: channelOn(old, kind, "email"),
        }));
        return next.map((row) => (kinds.includes(row.kind) ? { ...row, [channel]: value } : row));
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(PREFS_KEY, context.previous);
      toast.error("Couldn't save that setting");
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: PREFS_KEY }),
  });
};
